import { useMemo } from "react";
import { format } from "date-fns";
import { useTrackVisibility } from "@/hooks/use-track-visibility";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  ReferenceLine,
} from "recharts";
import { useIsMobile } from "@/hooks/use-mobile";
import { CashFlowChartData, Granularity } from "./interfaces";
import { formatCurrencyCompact, formatTooltipValue } from "./utils";

interface CashFlowChartProps {
  actualData: CashFlowChartData[];
  granularity: Granularity;
  dateRange: { startDate: string; endDate: string };
  loading?: boolean;
}

export const CashFlowChart = ({
  actualData,
  granularity,
  dateRange,
  loading = false,
}: CashFlowChartProps) => {
  const isMobile = useIsMobile();
  const visibilityRef = useTrackVisibility("cashflow_chart");

  const BIWEEKLY_EPOCH = new Date(2025, 0, 5);

  const snapToBiweekly = (date: Date): Date => {
    const days = Math.floor((date.getTime() - BIWEEKLY_EPOCH.getTime()) / 86_400_000);
    const idx = Math.floor(days / 14);
    return new Date(BIWEEKLY_EPOCH.getTime() + idx * 14 * 86_400_000);
  };

  const getPeriodLabel = (date: Date): string => {
    if (granularity === "monthly")
      return date.toLocaleDateString("en-US", { month: "short", year: "2-digit" });
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  };

  const { allPeriods, periodRangeMap } = useMemo(() => {
    const start = new Date(dateRange.startDate);
    const end = new Date(dateRange.endDate);
    const periods: string[] = [];
    const rangeMap = new Map<string, string>();
    const fmt = (d: Date) =>
      d.toLocaleDateString("en-US", { month: "short", day: "numeric" });

    if (granularity === "monthly") {
      let cur = new Date(start.getFullYear(), start.getMonth(), 1);
      const endM = new Date(end.getFullYear(), end.getMonth(), 1);
      while (cur <= endM) {
        const label = format(cur, "MMM yyyy");
        rangeMap.set(label, `${fmt(cur)} – ${fmt(new Date(cur.getFullYear(), cur.getMonth() + 1, 0))}`);
        periods.push(label);
        cur = new Date(cur.getFullYear(), cur.getMonth() + 1, 1);
      }
    } else if (granularity === "biweekly") {
      let cur = snapToBiweekly(start);
      while (cur <= end) {
        const periodEnd = new Date(cur.getTime() + 13 * 86_400_000);
        const label = getPeriodLabel(cur);
        rangeMap.set(label, `${fmt(cur)} – ${fmt(periodEnd)}`);
        periods.push(label);
        cur = new Date(cur.getTime() + 14 * 86_400_000);
      }
    } else {
      const cur = new Date(start);
      cur.setDate(cur.getDate() - cur.getDay());
      cur.setHours(0, 0, 0, 0);
      while (cur <= end) {
        const periodEnd = new Date(cur.getTime() + 6 * 86_400_000);
        const label = getPeriodLabel(cur);
        rangeMap.set(label, `${fmt(cur)} – ${fmt(periodEnd)}`);
        periods.push(label);
        cur.setDate(cur.getDate() + 7);
      }
    }
    return { allPeriods: periods, periodRangeMap: rangeMap };
  }, [dateRange, granularity]);

  // Aggregate sub-period data for weekly / biweekly buckets
  const aggregateByPeriod = (data: CashFlowChartData[]): CashFlowChartData[] => {
    if (granularity === "monthly") return data;
    const bucketMap = new Map<string, CashFlowChartData[]>();
    data.forEach((item) => {
      const date = new Date(item.month);
      const bucketStart =
        granularity === "biweekly"
          ? snapToBiweekly(date)
          : (() => {
              const d = new Date(date);
              d.setDate(date.getDate() - date.getDay());
              return d;
            })();
      const key = bucketStart.toISOString().split("T")[0];
      if (!bucketMap.has(key)) bucketMap.set(key, []);
      bucketMap.get(key)!.push(item);
    });
    return Array.from(bucketMap.entries())
      .sort(([a], [b]) => new Date(a).getTime() - new Date(b).getTime())
      .map(([bucketKey, items]) => ({
        month: getPeriodLabel(new Date(bucketKey)),
        inflows_usd: items.reduce((s, i) => s + (i.inflows_usd || 0), 0),
        outflows_usd: items.reduce((s, i) => s + (i.outflows_usd || 0), 0),
        net_flow_usd: items.reduce((s, i) => s + (i.net_flow_usd || 0), 0),
        cumulative_balance_usd: 0,
      }));
  };

  const processedData = aggregateByPeriod(actualData);
  const actualMap = new Map(processedData.map((d) => [d.month, d]));

  const displayData = allPeriods.map((month) => {
    const a = actualMap.get(month);
    return {
      month,
      periodRange: periodRangeMap.get(month) ?? month,
      inflows: a?.inflows_usd ?? 0,
      outflows: a?.outflows_usd ?? 0,
      net_flow: a?.net_flow_usd ?? 0,
    };
  });

  type Entry = (typeof displayData)[number];
  const CustomTooltip = ({
    active,
    payload,
  }: {
    active?: boolean;
    payload?: { payload: Entry }[];
  }) => {
    if (!active || !payload?.length) return null;
    const d = payload[0].payload;
    return (
      <div className="bg-white/95 backdrop-blur-sm border border-gray-200 rounded-xl shadow-xl p-4 text-sm">
        <p className="font-semibold text-gray-900 text-base">{d.month}</p>
        <p className="text-xs text-gray-400 mb-3">{d.periodRange}</p>
        <div className="space-y-2">
          <div className="flex items-center justify-between gap-6">
            <span className="flex items-center gap-2">
              <div className="w-3 h-3 rounded bg-emerald-500" />
              <span className="text-gray-600">Inflows</span>
            </span>
            <span className="font-semibold text-emerald-600">{formatTooltipValue(d.inflows)}</span>
          </div>
          <div className="flex items-center justify-between gap-6">
            <span className="flex items-center gap-2">
              <div className="w-3 h-3 rounded bg-rose-500" />
              <span className="text-gray-600">Outflows</span>
            </span>
            <span className="font-semibold text-rose-600">{formatTooltipValue(d.outflows)}</span>
          </div>
          <div className="flex items-center justify-between gap-6 pt-2 border-t border-gray-100">
            <span className="flex items-center gap-2">
              <div className="w-4 h-0.5 bg-orange-500 rounded" />
              <span className="text-gray-600">Net Flow</span>
            </span>
            <span className={`font-semibold ${d.net_flow >= 0 ? "text-emerald-600" : "text-rose-600"}`}>
              {formatTooltipValue(d.net_flow)}
            </span>
          </div>
        </div>
      </div>
    );
  };

  if (!loading && actualData.length === 0) {
    return (
      <Card ref={visibilityRef} className="shadow-sm border-border/60">
        <CardContent className="flex flex-col items-center justify-center py-16 text-center gap-2">
          <p className="text-sm font-medium text-muted-foreground">No cash flow data for this period</p>
          <p className="text-xs text-muted-foreground/70">
            No transactions found for the selected period.
          </p>
        </CardContent>
      </Card>
    );
  }

  const chartMargin = isMobile
    ? { top: 10, right: 10, left: 0, bottom: 5 }
    : { top: 16, right: 24, left: 20, bottom: 5 };

  return (
    <Card ref={visibilityRef} className="shadow-sm border-border/60">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between flex-wrap gap-2">
          <div>
            <CardTitle className="text-base font-semibold">Cash Flow</CardTitle>
            <p className="text-xs text-muted-foreground mt-1">
              Inflows, outflows and net flow · based on Bank Transactions
            </p>
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-2">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="flex flex-col items-center gap-3">
              <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
              <span className="text-xs text-muted-foreground">Loading…</span>
            </div>
          </div>
        ) : (
          <>
            <ResponsiveContainer width="100%" height={isMobile ? 300 : 380}>
              <ComposedChart data={displayData} margin={chartMargin}>
                <defs>
                  <linearGradient id="grad-green" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#34d399" />
                    <stop offset="100%" stopColor="#10b981" />
                  </linearGradient>
                  <linearGradient id="grad-red" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#fb7185" />
                    <stop offset="100%" stopColor="#f43f5e" />
                  </linearGradient>
                </defs>
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="#e5e7eb"
                  vertical={false}
                  opacity={0.5}
                />
                <XAxis
                  dataKey="month"
                  tick={{ fill: "#6b7280", fontSize: 11 }}
                  tickLine={false}
                  axisLine={{ stroke: "#d1d5db", strokeWidth: 1.5 }}
                  height={50}
                />
                <YAxis
                  tick={{ fill: "#6b7280", fontSize: 11 }}
                  tickLine={false}
                  axisLine={false}
                  tickFormatter={formatCurrencyCompact}
                  width={60}
                />
                <RechartsTooltip
                  content={<CustomTooltip />}
                  cursor={{ fill: "#f3f4f6", opacity: 0.3 }}
                />
                <ReferenceLine y={0} stroke="#94a3b8" strokeWidth={1.5} strokeDasharray="3 3" />

                <Bar dataKey="inflows" fill="url(#grad-green)" radius={[5, 5, 0, 0]} barSize={22} name="Inflows" />
                <Bar dataKey="outflows" fill="url(#grad-red)" radius={[5, 5, 0, 0]} barSize={22} name="Outflows" />
                <Line
                  type="monotone"
                  dataKey="net_flow"
                  stroke="#f97316"
                  strokeWidth={2.5}
                  strokeDasharray="6 3"
                  dot={false}
                  activeDot={{ r: 5, stroke: "#f97316", strokeWidth: 2.5, fill: "#fff" }}
                  name="Net Flow"
                />
              </ComposedChart>
            </ResponsiveContainer>

            <div className="flex flex-wrap items-center gap-5 mt-4 pt-3 border-t border-gray-100">
              <div className="flex items-center gap-2">
                <div className="w-3.5 h-3.5 rounded bg-emerald-500" />
                <span className="text-xs text-gray-600">Inflows</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3.5 h-3.5 rounded bg-rose-500" />
                <span className="text-xs text-gray-600">Outflows</span>
              </div>
              <div className="flex items-center gap-2">
                <div
                  className="w-5 h-0.5"
                  style={{
                    backgroundImage:
                      "repeating-linear-gradient(90deg,#f97316 0,#f97316 5px,transparent 5px,transparent 8px)",
                  }}
                />
                <span className="text-xs text-gray-600">Net Flow</span>
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};
